// src/oura/components/docs/write/EditClientEmailDialog.tsx
//
// Edit the client email in a dialog wide enough to read it (#4840): who it
// goes to, the subject, and the body in a rich text editor that starts from
// the email as the preview shows it. Save hands the view the edited email in
// its markup; the view then previews it through the server and runs the
// checks again, so what is sent is what was edited. Cancel changes nothing.
//
// It looks like a mail app's compose window (#4853, after Gmail's): a title
// bar with a close, To / Cc / Subject as plain rows, the body filling the rest,
// the tools in a rounded bar under it, Save at the bottom left and Discard at
// the bottom right. Save, not Send: Send keeps its own confirmation and the
// server's checks. No Bcc, as the send takes only To and Cc. On a phone it
// takes the whole screen.
//
// Same shell as the other dialogs here: a portal on <body>, Escape cancels,
// clicks and keys stay inside the dialog.

import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Trash2Icon, XIcon } from "lucide-react";
import { Button } from "../../../primitives/Button";
import { cn } from "../../../lib/cn";
import { readRecipients, type MessageErrors, type MessageText } from "../../../onboarding/messageText";
import { ClientEmailEditor } from "./ClientEmailEditor";
import { htmlToMarkup } from "./emailMarkup";

// 16px on a phone: smaller text makes iPhones zoom in on a tap.
const ROW_INPUT = "h-10 min-w-0 flex-1 border-0 bg-transparent p-0 text-base text-gray-900 outline-none placeholder:text-gray-500 sm:text-sm";

/** One compose row: an inline label, the field, and its error under it. */
function Row({ id, label, labelHidden, error, children, end }: { id: string; label: string; labelHidden?: boolean; error?: string; children: ReactNode; end?: ReactNode }) {
    return (
        <div className={cn("border-b", error ? "border-rose-300" : "border-gray-200")}>
            <div className="flex items-center gap-2 px-4">
                <label htmlFor={id} className={labelHidden ? "sr-only" : "flex-shrink-0 text-sm text-gray-500"}>
                    {label}
                </label>
                {children}
                {end}
            </div>
            {error && <p className="m-0 px-4 pb-2 text-xs text-rose-600">{error}</p>}
        </div>
    );
}

export function EditClientEmailDialog({
    docId,
    value,
    html,
    onSave,
    onClose,
}: {
    docId: string;
    /** The email as it stands: To and Copy as typed, the subject, the body's markup. */
    value: MessageText;
    /** That body as the preview shows it: the editor starts from it. */
    html: string;
    onSave: (next: MessageText) => void;
    onClose: () => void;
}) {
    const [to, setTo] = useState(value.to);
    const [cc, setCc] = useState(value.cc);
    // The Cc row shows when the email has a copy, or when asked for.
    const [showCc, setShowCc] = useState(() => value.cc.trim() !== "");
    const [subject, setSubject] = useState(value.subject);
    const [bodyHtml, setBodyHtml] = useState<string | null>(null);
    const [errors, setErrors] = useState<MessageErrors | null>(null);
    const titleId = `edit-client-email-title-${docId}`;
    const bodyId = `edit-client-email-body-${docId}`;
    const toId = `cem-to-${docId}`;
    const ccId = `cem-cc-${docId}`;
    const subjectId = `cem-subject-${docId}`;

    const save = () => {
        const people = readRecipients({ to, cc, subject, body: "" });
        const next: MessageErrors = { ...(people.errors ?? {}) };
        if (!subject.trim()) next.subject = "Add a subject.";
        // An untouched editor keeps the markup exactly as it was.
        const body = bodyHtml === null ? value.body : htmlToMarkup(bodyHtml);
        if (!body.trim()) next.body = "The email is empty.";
        if (Object.keys(next).length > 0) {
            if (next.cc) setShowCc(true);
            setErrors(next);
            return;
        }
        onSave({ to, cc, subject: subject.trim(), body });
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-edit-client-email-dialog={docId}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Escape") onClose();
            }}
        >
            <div className="absolute inset-0 bg-gray-900/50" onClick={onClose} />

            <div className="relative flex h-full w-full flex-col overflow-hidden bg-white pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] shadow-2xl sm:h-[85vh] sm:max-w-4xl sm:rounded-xl sm:py-0">
                <div className="flex items-center justify-between gap-2 bg-slate-100 py-1.5 pl-4 pr-2">
                    <h2 id={titleId} className="m-0 text-sm font-medium text-gray-900">
                        Edit the email
                    </h2>
                    <button type="button" title="Close" aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-200" onClick={onClose}>
                        <XIcon className="h-4 w-4" aria-hidden />
                    </button>
                </div>

                <Row
                    id={toId}
                    label="To"
                    error={errors?.to}
                    end={
                        !showCc && (
                            <button type="button" className="flex-shrink-0 text-sm text-gray-600 hover:text-gray-900 hover:underline" onClick={() => setShowCc(true)}>
                                Cc
                            </button>
                        )
                    }
                >
                    <input id={toId} className={ROW_INPUT} value={to} onChange={(e) => setTo(e.target.value)} autoComplete="off" aria-invalid={!!errors?.to} />
                </Row>
                {showCc && (
                    <Row id={ccId} label="Cc" error={errors?.cc}>
                        <input id={ccId} className={ROW_INPUT} value={cc} onChange={(e) => setCc(e.target.value)} autoComplete="off" aria-invalid={!!errors?.cc} />
                    </Row>
                )}
                <Row id={subjectId} label="Subject" labelHidden error={errors?.subject}>
                    <input id={subjectId} className={ROW_INPUT} placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} autoComplete="off" aria-invalid={!!errors?.subject} />
                </Row>

                <p id={bodyId} className="sr-only">
                    Email
                </p>
                <ClientEmailEditor html={html} onChange={setBodyHtml} labelledBy={bodyId} />
                {errors?.body && <p className="m-0 px-4 pb-1 text-xs text-rose-600">{errors.body}</p>}

                <div className="flex items-center gap-3 px-4 pb-3 pt-1">
                    <Button className="flex-shrink-0 rounded-full px-6" writes onClick={save} data-edit-client-email-save={docId}>
                        Save changes
                    </Button>
                    <p className="m-0 hidden min-w-0 flex-1 text-xs text-gray-500 sm:block">
                        The doc&rsquo;s link stays a link; its words can change. Shift+Enter starts a new line in the same paragraph.
                    </p>
                    <button
                        type="button"
                        title="Discard changes"
                        aria-label="Discard changes"
                        className="ml-auto flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100"
                        onClick={onClose}
                    >
                        <Trash2Icon className="h-4 w-4" aria-hidden />
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
